import type { Session, SessionUser } from "@/types";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { apiFetch } from "@/services/api";

/**
 * Replaces `next-auth/react`.
 *
 * The session is no longer managed by a Next.js route handler: the NestJS API
 * exposes the equivalent endpoints under `/api/auth/*`, and the httpOnly
 * `mova.session-token` cookie is the single source of truth. The exported names,
 * the `status` tri-state and the return shapes are all identical to NextAuth's,
 * so `useSession()`, `signIn()`, `signOut()` and `update()` are called exactly
 * as before.
 *
 * Endpoints used:
 *   GET  /api/auth/session   → `{ user, expires }` or `null`
 *   POST /api/auth/login     → sets the cookie, returns the session
 *   POST /api/auth/logout    → clears the cookie
 */

type SessionStatus = "loading" | "authenticated" | "unauthenticated";

type SignInResult = {
  error?: string;
  ok?: boolean;
  status?: number;
  url?: string;
  role?: string;
};

type UpdateArgs = { name?: string | null; email?: string | null; image?: string | null };

type AuthContextValue = {
  data: Session | null;
  session: Session | null;
  status: SessionStatus;
  update: (args?: UpdateArgs) => Promise<Session | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/** Broadcast so every `useSession()` consumer refreshes after a sign-in/out. */
const SESSION_EVENT = "mova-session-changed";

function broadcastSessionChange(action: "update" | "clear") {
  window.dispatchEvent(new CustomEvent(SESSION_EVENT, { detail: { action } }));
}

export function SessionProvider({
  children,
  /** NextAuth accepted this to skip the initial fetch; kept for compatibility. */
  session: initialSession = null,
}: {
  children: ReactNode;
  session?: Session | null;
}) {
  const [session, setSession] = useState<Session | null>(initialSession);
  const [status, setStatus] = useState<SessionStatus>(
    initialSession ? "authenticated" : "loading",
  );
  const mounted = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<Session | null>("/api/auth/session", { method: "GET" });
      setSession(data ?? null);
      setStatus(data?.user ? "authenticated" : "unauthenticated");
      return data ?? null;
    } catch {
      // A failed session probe means "not signed in", which is what NextAuth
      // surfaced to the UI when the endpoint errored.
      setSession(null);
      setStatus("unauthenticated");
      return null;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refresh();

    // Sign-in and sign-out happen in another tab/component; keep every tab in
    // sync the way NextAuth's `useSession` did with its storage event.
    const onFocus = () => void refresh();
    const onStorage = (event: StorageEvent) => {
      if (event.key === "mova-session") void refresh();
    };
    const onSessionChange = (event: Event) => {
      const detail = (event as CustomEvent<{ action: string }>).detail;
      if (!detail || detail.action !== "update") return;
      void refresh();
    };

    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);
    window.addEventListener(SESSION_EVENT, onSessionChange);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SESSION_EVENT, onSessionChange);
    };
  }, [refresh]);

  const update = useCallback(
    async (args: UpdateArgs = {}) => {
      const next = await refresh();
      if (!next?.user) return null;
      return { ...next, user: { ...next.user, ...args } };
    },
    [refresh],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ data: session, session, status, update }),
    [session, status, update],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useSession(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useSession must be used inside a <SessionProvider>.");
  }
  return context;
}

type CredentialSignIn = {
  email: string;
  password: string;
  redirect?: boolean;
  callbackUrl?: string;
};

/**
 * `signIn("credentials", { … })` posts to `/api/auth/login`; the OAuth provider
 * names redirect straight to the API's start endpoint, which then bounces back
 * into the SPA. Either way the caller ends up with NextAuth's result shape.
 */
export async function signIn(
  provider: "credentials" | "google" | "github",
  options: CredentialSignIn | { callbackUrl?: string; redirect?: boolean } = {},
): Promise<SignInResult> {
  const callbackUrl =
    "callbackUrl" in options && typeof options.callbackUrl === "string"
      ? options.callbackUrl
      : "/dashboard";

  if (provider !== "credentials") {
    if (typeof window !== "undefined") {
      window.location.href = `/api/auth/${provider}?callbackUrl=${encodeURIComponent(callbackUrl)}`;
    }
    return { ok: true };
  }

  const { email, password } = options as CredentialSignIn;

  try {
    const session = await apiFetch<Session>("/api/auth/login", {
      method: "POST",
      body: { email, password },
    });

    // Let every other `useSession()` consumer pick up the new cookie.
    window.localStorage.setItem("mova-session", String(Date.now()));
    broadcastSessionChange("update");

    if (session?.user?.id) {
      return { ok: true, status: 200, role: session.user.role };
    }
    return { error: "Invalid email or password." };
  } catch (error) {
    // Network-level failure (backend unreachable) — fetch throws a TypeError
    // with no HTTP status. Distinguish it from a real 401 so the user gets a
    // useful message instead of "Invalid email or password."
    if (error instanceof TypeError && !("status" in error)) {
      return { error: "Backend not connected. Please try again later." };
    }
    const message = error instanceof Error ? error.message : "Invalid email or password.";
    return { error: message };
  }
}

export async function signOut(options: { callbackUrl?: string } = {}): Promise<SignInResult> {
  try {
    await apiFetch("/api/auth/logout", { method: "POST" });
  } catch {
    // Even if the call failed, clear local state so the UI is not stuck.
  }

  window.localStorage.removeItem("mova-session");
  broadcastSessionChange("clear");

  const callbackUrl = options.callbackUrl ?? "/";
  if (typeof window !== "undefined") {
    window.location.href = callbackUrl;
  }

  return { ok: true };
}

export type { Session, SessionStatus, SessionUser };